import { execSync } from 'node:child_process';
import { existsSync, readFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { isAuthenticated, getDefaultConfigDir } from './auth.js';
import { apiGet } from './api-client.js';
import { GATEWAY_PORT } from './gateway-lifecycle.js';
import { detectInstalledTools } from './hooks-config.js';

/**
 * Data model for the four core onboarding steps, shared (by step-ID list +
 * install-monotonicity) with the web reducer `deriveSetupProgress()`.
 *
 * The two surfaces are separate implementations by necessity, but they no
 * longer read *different sources* for the same fact:
 *   - Server-observable signals (cli-worker heartbeat / captured session) are
 *     unified through the shared `GET /api/setup/status` endpoint — the CLI's
 *     `workerActive()` and the web's `use-setup-progress.ts` both bottom out on
 *     that one server-side authority, so they cannot drift on it.
 *   - Local signals (auth = `~/.config/rulemetric` creds via `isAuthenticated()`,
 *     hooks = cwd `.claude/settings.json`) stay machine-local: the browser
 *     cannot read the user's filesystem, so these legitimately differ from the
 *     web's account-scoped proxies for the same step (see the design doc's
 *     "Shared step model, two implementations").
 * Each step exposes:
 *   - detect(): is it done? (reads a local/remote signal, never mutates)
 *   - plan():   what would apply() write? (read-only, returns diffs/artifacts)
 *   - apply():  perform the step (thin — shells out to existing commands)
 */

export const CORE_STEP_IDS = ['install', 'auth', 'hooks', 'worker'] as const;
export type StepId = (typeof CORE_STEP_IDS)[number];

export type ArtifactKind = 'file' | 'command' | 'service';

export interface Artifact {
  path: string;
  kind: ArtifactKind;
  before: string | null;
  after: string | null;
  /** True when `after` contains a masked secret (never the raw value). */
  masked?: boolean;
}

export interface StepPlan {
  id: StepId;
  title: string;
  command?: string;
  artifacts: Artifact[];
}

export interface SetupContext {
  projectPath: string;
  configDir: string;
}

export interface SetupStep {
  id: StepId;
  title: string;
  command?: string;
  detect(ctx: SetupContext): Promise<boolean>;
  plan(ctx: SetupContext): Promise<StepPlan>;
  apply(ctx: SetupContext): Promise<void>;
}

export interface StepStatus {
  id: StepId;
  done: boolean;
}

export interface SetupStatus {
  steps: StepStatus[];
  coreComplete: boolean;
  currentStepId: StepId | null;
}

/** Build the default context from the current process (cwd + config dir). */
export function defaultContext(): SetupContext {
  return {
    projectPath: process.cwd(),
    configDir: getDefaultConfigDir(),
  };
}

const CERT_PATH = join(homedir(), '.mitmproxy', 'mitmproxy-ca-cert.pem');

/**
 * Mask a secret: keep the first 4 and last 4 characters, replace the middle
 * with asterisks. Strings too short to reveal safely are fully masked.
 */
export function maskToken(s: string): string {
  if (!s) return '';
  if (s.length <= 8) return '*'.repeat(s.length);
  const head = s.slice(0, 4);
  const tail = s.slice(-4);
  return `${head}${'*'.repeat(Math.max(4, s.length - 8))}${tail}`;
}

/**
 * Simple line-oriented +/- diff for terminal display. Not a real LCS — good
 * enough to show a human/agent exactly which lines an artifact would change.
 */
export function renderDiff(before: string | null, after: string | null): string {
  const beforeLines = before === null ? [] : before.replace(/\n$/, '').split('\n');
  const afterLines = after === null ? [] : after.replace(/\n$/, '').split('\n');
  const out: string[] = [];
  const max = Math.max(beforeLines.length, afterLines.length);
  for (let i = 0; i < max; i++) {
    const b = beforeLines[i];
    const a = afterLines[i];
    if (b === a) {
      out.push(` ${b}`);
    } else {
      if (b !== undefined) out.push(`-${b}`);
      if (a !== undefined) out.push(`+${a}`);
    }
  }
  return out.join('\n');
}

/** Read a single KEY=value from the env file, or null if absent. */
function readEnvValue(configDir: string, key: string): string | null {
  const envPath = join(configDir, 'env');
  if (!existsSync(envPath)) return null;
  try {
    const content = readFileSync(envPath, 'utf-8');
    const match = content.match(new RegExp(`^${key}=(.+)$`, 'm'));
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Is a CLI worker heartbeat active for the caller? (best-effort, network)
 *
 * Reads the shared `GET /api/setup/status` endpoint's `workerActive` field
 * rather than re-deriving it from `/api/workers/status`. That endpoint computes
 * the cli-worker heartbeat signal server-side using the SAME window +
 * staleness thresholds + `workerType === 'cli-worker'` filter the web strip
 * uses, so the CLI and web can't drift on this fact by construction (they used
 * to each filter/threshold client-side, which is exactly how they could
 * disagree). Fast-fails after 2.5s so `setup --json` / `setup plan` degrade to
 * "not running" instead of blocking on a stalled connection — detection is a
 * UX signal, not a correctness gate; offline → false.
 */
async function workerActive(): Promise<boolean> {
  if (!isAuthenticated()) return false;
  try {
    const status = await Promise.race([
      apiGet<{
        cliEverConnected?: boolean;
        anyCapturedSession?: boolean;
        workerActive?: boolean;
      }>('/api/setup/status'),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('setup/status timeout')), 2500),
      ),
    ]);
    return status?.workerActive === true;
  } catch {
    return false;
  }
}

/**
 * Compute the in-memory `.claude/settings.json` after the hooks env block is
 * merged, WITHOUT touching disk. Replicates the read-only shape of
 * `hooks install` §2 (env.HTTPS_PROXY / NO_PROXY / NODE_EXTRA_CA_CERTS).
 */
function projectHooksSettings(projectPath: string): { before: string | null; after: string } {
  const settingsPath = join(projectPath, '.claude', 'settings.json');
  let settings: Record<string, unknown> = {};
  let before: string | null = null;
  if (existsSync(settingsPath)) {
    try {
      before = readFileSync(settingsPath, 'utf-8').replace(/\n$/, '');
      settings = JSON.parse(before) as Record<string, unknown>;
    } catch {
      settings = {};
    }
  }
  const env = { ...((settings.env as Record<string, string>) ?? {}) };
  env.HTTPS_PROXY = `http://localhost:${GATEWAY_PORT}`;
  env.NO_PROXY = 'localhost,127.0.0.1,*.supabase.co,*.supabase.in';
  if (existsSync(CERT_PATH)) {
    env.NODE_EXTRA_CA_CERTS = CERT_PATH;
  }
  const after = JSON.stringify({ ...settings, env }, null, 2);
  return { before, after };
}

const WORKER_PLIST_PATH = join(homedir(), 'Library', 'LaunchAgents', 'com.rulemetric.worker.plist');
const WORKER_SYSTEMD_PATH = join(homedir(), '.config', 'systemd', 'user', 'rulemetric-worker.service');

/** Run a command with inherited stdio (interactive-friendly). Thin apply(). */
function runCommand(cmd: string): void {
  execSync(cmd, { stdio: 'inherit' });
}

export const STEPS: SetupStep[] = [
  {
    id: 'install',
    title: 'Install the CLI',
    command: 'npm install -g @rulemetric/cli',
    // Monotonic: the CLI must exist to produce any downstream signal, so it
    // greens off auth or worker presence (nothing local phones home before).
    async detect(): Promise<boolean> {
      return isAuthenticated() || (await workerActive());
    },
    async plan(): Promise<StepPlan> {
      return {
        id: 'install',
        title: 'Install the CLI',
        command: 'npm install -g @rulemetric/cli',
        artifacts: [
          {
            path: 'npm install -g @rulemetric/cli',
            kind: 'command',
            before: null,
            after: 'rulemetric --version   # verifies the binary is on PATH',
          },
        ],
      };
    },
    async apply(): Promise<void> {
      runCommand('npm install -g @rulemetric/cli');
    },
  },
  {
    id: 'auth',
    title: 'Authenticate',
    command: 'rulemetric auth login',
    async detect(): Promise<boolean> {
      return isAuthenticated();
    },
    async plan(ctx): Promise<StepPlan> {
      const envPath = join(ctx.configDir, 'env');
      // Guard the read like the sibling helpers (readEnvValue /
      // projectHooksSettings) do: a TOCTOU race or permission error between
      // existsSync and readFileSync must NOT throw out of plan(). Fall back to
      // a secret-free marker rather than crashing `setup plan`/`--show`.
      let before: string | null = null;
      if (existsSync(envPath)) {
        try {
          before = maskEnvFile(readFileSync(envPath, 'utf-8'));
        } catch {
          before = '(unreadable)';
        }
      }
      const rawToken = readEnvValue(ctx.configDir, 'RULEMETRIC_ACCESS_TOKEN');
      const tokenDisplay = rawToken ? maskToken(rawToken) : maskToken('<token-from-login>');
      // Reconstruct EXACTLY what `auth login`'s saveEnvFile writes so the diff
      // doesn't show phantom changes: line order [TOKEN, KEY?, URL?], the key
      // preserved (masked) when present, and the URL sourced from
      // process.env.RULEMETRIC_API_URL — the same value login passes — and OMITTED
      // when unset (login writes no URL line then). All masking preserved; the raw
      // token/key are never emitted.
      const afterLines = [`RULEMETRIC_ACCESS_TOKEN=${tokenDisplay}`];
      const rawApiKey = readEnvValue(ctx.configDir, 'RULEMETRIC_API_KEY');
      if (rawApiKey) {
        afterLines.push(`RULEMETRIC_API_KEY=${maskToken(rawApiKey)}`);
      }
      const loginUrl = process.env.RULEMETRIC_API_URL;
      if (loginUrl) {
        afterLines.push(`RULEMETRIC_API_URL=${loginUrl}`);
      }
      const after = afterLines.join('\n');
      return {
        id: 'auth',
        title: 'Authenticate',
        command: 'rulemetric auth login',
        artifacts: [
          {
            path: envPath,
            kind: 'file',
            before,
            after,
            masked: true,
          },
        ],
      };
    },
    async apply(): Promise<void> {
      runCommand('rulemetric auth login');
    },
  },
  {
    id: 'hooks',
    title: 'Install hooks',
    command: 'rulemetric hooks install',
    async detect(ctx): Promise<boolean> {
      const settingsPath = join(ctx.projectPath, '.claude', 'settings.json');
      if (!existsSync(settingsPath)) return false;
      try {
        const settings = JSON.parse(readFileSync(settingsPath, 'utf-8')) as Record<string, unknown>;
        const env = (settings.env ?? {}) as Record<string, unknown>;
        return typeof env.HTTPS_PROXY === 'string';
      } catch {
        return false;
      }
    },
    async plan(ctx): Promise<StepPlan> {
      const settingsPath = join(ctx.projectPath, '.claude', 'settings.json');
      const { before, after } = projectHooksSettings(ctx.projectPath);
      const artifacts: Artifact[] = [
        { path: settingsPath, kind: 'file', before, after },
      ];
      // Note additional tool surfaces the real install would also touch.
      const tools = detectInstalledTools(ctx.projectPath).filter((t) => t !== 'claude_code');
      for (const tool of tools) {
        artifacts.push({
          path: `${ctx.projectPath} (${tool} proxy config)`,
          kind: 'file',
          before: null,
          after: `proxy → http://localhost:${GATEWAY_PORT}`,
        });
      }
      return {
        id: 'hooks',
        title: 'Install hooks',
        command: 'rulemetric hooks install',
        artifacts,
      };
    },
    async apply(): Promise<void> {
      runCommand('rulemetric hooks install');
    },
  },
  {
    id: 'worker',
    title: 'Run the worker',
    command: 'rulemetric service install --worker-only',
    async detect(): Promise<boolean> {
      return workerActive();
    },
    async plan(): Promise<StepPlan> {
      const isMac = process.platform === 'darwin';
      const servicePath = isMac ? WORKER_PLIST_PATH : WORKER_SYSTEMD_PATH;
      // SECURITY: never read the installed plist/systemd unit into `before` —
      // those files embed RULEMETRIC_ACCESS_TOKEN / RULEMETRIC_API_KEY verbatim
      // (service/install.ts writes them from ALLOWED_ENV_VARS), so echoing the
      // file contents would leak the raw secret through `plan`, `--show`, and
      // `--json`. Use a synthetic, secret-free marker for the current state.
      const before = existsSync(servicePath)
        ? `(service already installed at ${servicePath})`
        : null;
      // Concise representation (path + ExecStart node/cli invocation) rather
      // than reconstructing the full plist — keep read-only + legible.
      const after = isMac
        ? [
            `Label: com.rulemetric.worker`,
            `ProgramArguments: <node> <cli>/bin/run.js evals agent`,
            `RunAtLoad: true, KeepAlive: true`,
          ].join('\n')
        : [
            `[Service]`,
            `ExecStart=<node> <cli>/bin/run.js evals agent`,
            `Restart=on-failure`,
          ].join('\n');
      return {
        id: 'worker',
        title: 'Run the worker',
        command: 'rulemetric service install --worker-only',
        artifacts: [
          { path: servicePath, kind: 'service', before, after },
        ],
      };
    },
    async apply(): Promise<void> {
      runCommand('rulemetric service install --worker-only');
    },
  },
];

const STEP_BY_ID = new Map<StepId, SetupStep>(STEPS.map((s) => [s.id, s]));

export function getStep(id: StepId): SetupStep {
  const step = STEP_BY_ID.get(id);
  if (!step) throw new Error(`Unknown setup step: ${id}`);
  return step;
}

export function isStepId(value: string): value is StepId {
  return (CORE_STEP_IDS as readonly string[]).includes(value);
}

/**
 * Keys whose values are safe to show verbatim in the env-file diff. Everything
 * NOT in this set is masked. Deny-by-default is deliberate: `~/.config/rulemetric/env`
 * can legitimately hold DATABASE_URL, SUPABASE_SERVICE_ROLE_KEY, SUPABASE_JWT_SECRET,
 * GITHUB_TOKEN, RESEND_API_KEY, etc. (see ALLOWED_ENV_VARS in service/install.ts),
 * so an allowlist-to-hide would leak any secret we forgot to enumerate. Revealing
 * only known-safe keys means a future secret added to the file is masked automatically.
 */
const SAFE_ENV_KEYS = new Set([
  'RULEMETRIC_API_URL',
  'RULEMETRIC_USER_ID',
  'RULEMETRIC_CLIENT_NAME',
  'RESEND_FROM_EMAIL',
  'SENTRY_DSN', // a DSN is not a secret
  'PG_POOL_MAX',
  'PG_IDLE_TIMEOUT',
  'PG_STATEMENT_TIMEOUT_MS',
  'NODE_ENV',
]);

/**
 * Mask secret-bearing lines in a raw env file for display. Deny-by-default:
 * every `KEY=value` line has its value masked unless KEY is in SAFE_ENV_KEYS.
 * Non-assignment lines (comments, blanks) pass through untouched.
 */
function maskEnvFile(content: string): string {
  return content
    .replace(/\n$/, '')
    .split('\n')
    .map((line) => {
      const match = line.match(/^([A-Z0-9_]+)=(.+)$/);
      if (!match) return line; // comment / blank / non-assignment
      const [, key, value] = match;
      return SAFE_ENV_KEYS.has(key) ? line : `${key}=${maskToken(value)}`;
    })
    .join('\n');
}

/**
 * Run every step's detect() and derive the overall status. `currentStepId` is
 * the first incomplete step in core order (mirrors the web reducer).
 */
export async function computeStatus(ctx: SetupContext = defaultContext()): Promise<SetupStatus> {
  const steps: StepStatus[] = [];
  for (const step of STEPS) {
    // Sequential: detect() may hit the network; order is stable + cheap.
    // eslint-disable-next-line no-await-in-loop
    const done = await step.detect(ctx);
    steps.push({ id: step.id, done });
  }
  // Monotonicity (mirrors the web reducer's `cliDetected`): the CLI binary must
  // exist to produce ANY downstream signal, so `install` greens whenever auth,
  // hooks, or worker is detected — even if `install.detect()` alone (which only
  // sees auth/worker, not hooks) came back false. Without this, a user with
  // hooks installed but no live worker sees "Install the CLI: waiting" while
  // "Install hooks: done" — a contradiction.
  const install = steps.find((s) => s.id === 'install');
  if (install && !install.done) {
    install.done = steps.some((s) => s.id !== 'install' && s.done);
  }

  const currentStepId = steps.find((s) => !s.done)?.id ?? null;
  const coreComplete = steps.every((s) => s.done);
  return { steps, coreComplete, currentStepId };
}
